import { MetaLayout } from '@/@vxapp/@Enums/MetaLayout'
import { Endpoint } from '@/@vxapp/@Enums/Endpoint'
import type { EndpointSchema } from '@/@vxapp/@Enums/Endpoint'
import { A } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { Subject as GeneratedSubject } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import { BankingAbilitySubject } from '@/@vxapp/Banking/BankingAbilitySubject'
import { AiWorkspaceShortcut } from '@/@vxapp/@Enums/KeyboardShortcut'
import type { KeyboardShortcutId } from '@/@vxapp/@Enums/KeyboardShortcut'
import { themeConfig } from '@themeConfig'

const Subject = {
  ...GeneratedSubject,
  ...BankingAbilitySubject,
  EVENTS_GUID: 'Events:Guid',
} as const

// Lazy-loaded page component type
export type PageComponent = () => Promise<unknown>

export interface PageRouteMeta {
  subject: string
  layout?: MetaLayout | null
  action?: string
  redirectIfLoggedIn?: boolean
  /** Page-specific keyboard shortcuts; global shortcuts apply everywhere and are not listed. */
  shortcuts?: readonly KeyboardShortcutId[]
  [key: string]: unknown
}

export type PageMetaEndpoint = EndpointSchema[keyof EndpointSchema]

// Common structure for a single page metadata entry
export interface PageMetaItem {
  Title?: string
  Name?: string | null
  title?: string
  name?: string | null
  Icon?: string
  icon?: string
  Tooltip?: string | null
  Meta?: PageRouteMeta
  meta?: PageRouteMeta
  Path?: string
  path?: string
  Component?: PageComponent
  component?: PageComponent
  Endpoint?: PageMetaEndpoint
  [key: string]: unknown
}

// Group of pages under a single section
export interface PageMetaGroup {
  Endpoint?: PageMetaEndpoint
  Icon?: string
  icon?: string
  [key: string]: PageMetaItem | PageMetaEndpoint | string | undefined
}

export type PageMetaEndpointGroup = PageMetaGroup & { Endpoint: PageMetaEndpoint }

export interface PageMetaSchema {
  Auth: PageMetaGroup
  Dashboard: PageMetaGroup
  AiWorkspace: PageMetaGroup
  Orders: PageMetaEndpointGroup
  Invoices: PageMetaEndpointGroup
  Quotes: PageMetaEndpointGroup
  JobTypes: PageMetaGroup
  Projects: PageMetaGroup

  Banking: PageMetaGroup
  Sbr: PageMetaGroup

  // Assets
  Assets: PageMetaGroup
  Services: PageMetaGroup
  Inspections: PageMetaGroup

  // CRM
  Calendar: PageMetaEndpointGroup
  Customers: PageMetaGroup
  Messages: PageMetaGroup

  // Booking Rules
  Rules: PageMetaEndpointGroup

  // Setup
  Businesses: PageMetaGroup
  RevenueStreams: PageMetaGroup
  Tools: PageMetaGroup

  // Products
  Products: PageMetaEndpointGroup
  Categories: PageMetaEndpointGroup
  BookingOptions: PageMetaGroup
  Selections: PageMetaGroup

  // Email
  EmailAccounts: PageMetaGroup
  Templates: PageMetaGroup
  Themes: PageMetaGroup
  Events: PageMetaGroup

  // Access Control
  TenantUsers: PageMetaEndpointGroup
  TenantRoles: PageMetaEndpointGroup
  TenantRolePermissions: PageMetaEndpointGroup
  TenantPermissionDefinitions: PageMetaEndpointGroup
  MicrosoftOAuth: PageMetaGroup
  Sites: PageMetaEndpointGroup

}

// Dynamic imports for page components
const OrderSP: PageComponent = () => import('@/pages/service/o/[guid].vue')
const QuoteSP: PageComponent = () => import('@/pages/service/q/[guid].vue')
const InvoiceSP: PageComponent = () => import('@/pages/service/i/[guid].vue')

const pageMeta = {
  TenantUsers: {
    Endpoint: Endpoint.TenantUsers,
    List: { Title: 'TenantUsers', Name: 'setup-tenant-users-list', Tooltip: 'TenantUsers', Meta: { subject: Subject.TENANT_USERS, action: A.READ, layout: MetaLayout.Default } },
    Preview: { Title: 'TenantUsers', Name: 'setup-tenant-users-view-guid', Tooltip: 'TenantUsers', Meta: { subject: Subject.TENANT_USERS_GUID, action: A.READ, layout: MetaLayout.Default } },
    Add: { Title: 'Create TenantUsers', Name: 'setup-tenant-users-add', Tooltip: 'Create TenantUsers', Meta: { subject: Subject.TENANT_USERS, action: A.CREATE, layout: MetaLayout.Default } },
    Edit: { Title: 'Edit TenantUsers', Name: 'setup-tenant-users-edit-guid', Tooltip: 'Edit TenantUsers', Meta: { subject: Subject.TENANT_USERS_UPDATE_GUID, action: A.UPDATE, layout: MetaLayout.Default } },
  },
  TenantRoles: {
    Endpoint: Endpoint.TenantRoles,
    List: { Title: 'TenantRoles', Name: 'setup-tenant-roles-list', Tooltip: 'TenantRoles', Meta: { subject: Subject.TENANT_ROLES, action: A.READ, layout: MetaLayout.Default } },
    Preview: { Title: 'TenantRoles', Name: 'setup-tenant-roles-view-guid', Tooltip: 'TenantRoles', Meta: { subject: Subject.TENANT_ROLES_GUID, action: A.READ, layout: MetaLayout.Default } },
    Add: { Title: 'Create TenantRoles', Name: 'setup-tenant-roles-add', Tooltip: 'Create TenantRoles', Meta: { subject: Subject.TENANT_ROLES, action: A.CREATE, layout: MetaLayout.Default } },
    Edit: { Title: 'Edit TenantRoles', Name: 'setup-tenant-roles-edit-guid', Tooltip: 'Edit TenantRoles', Meta: { subject: Subject.TENANT_ROLES_UPDATE_GUID, action: A.UPDATE, layout: MetaLayout.Default } },
  },
  TenantRolePermissions: {
    Endpoint: Endpoint.TenantRolePermissions,
    List: { Title: 'TenantRolePermissions', Name: 'setup-tenant-role-permissions-list', Tooltip: 'TenantRolePermissions', Meta: { subject: Subject.TENANT_ROLE_PERMISSIONS, action: A.READ, layout: MetaLayout.Default } },
    Preview: { Title: 'TenantRolePermissions', Name: 'setup-tenant-role-permissions-view-guid', Tooltip: 'TenantRolePermissions', Meta: { subject: Subject.TENANT_ROLE_PERMISSIONS_GUID, action: A.READ, layout: MetaLayout.Default } },
    Add: { Title: 'Create TenantRolePermissions', Name: 'setup-tenant-role-permissions-add', Tooltip: 'Create TenantRolePermissions', Meta: { subject: Subject.TENANT_ROLE_PERMISSIONS, action: A.CREATE, layout: MetaLayout.Default } },
    Edit: { Title: 'Edit TenantRolePermissions', Name: 'setup-tenant-role-permissions-edit-guid', Tooltip: 'Edit TenantRolePermissions', Meta: { subject: Subject.TENANT_ROLE_PERMISSIONS_UPDATE_GUID, action: A.UPDATE, layout: MetaLayout.Default } },
  },
  TenantPermissionDefinitions: {
    Endpoint: Endpoint.TenantPermissionDefinitions,
    List: { Title: 'TenantPermissionDefinitions', Name: 'setup-tenant-permission-definitions-list', Tooltip: 'TenantPermissionDefinitions', Meta: { subject: Subject.TENANT_PERMISSION_DEFINITIONS, action: A.READ, layout: MetaLayout.Default } },
    Preview: { Title: 'TenantPermissionDefinitions', Name: 'setup-tenant-permission-definitions-view-guid', Tooltip: 'TenantPermissionDefinitions', Meta: { subject: Subject.TENANT_PERMISSION_DEFINITIONS_GUID, action: A.READ, layout: MetaLayout.Default } },
    Add: { Title: 'Create TenantPermissionDefinitions', Name: 'setup-tenant-permission-definitions-add', Tooltip: 'Create TenantPermissionDefinitions', Meta: { subject: Subject.TENANT_PERMISSION_DEFINITIONS, action: A.CREATE, layout: MetaLayout.Default } },
    Edit: { Title: 'Edit TenantPermissionDefinitions', Name: 'setup-tenant-permission-definitions-edit-guid', Tooltip: 'Edit TenantPermissionDefinitions', Meta: { subject: Subject.TENANT_PERMISSION_DEFINITIONS_UPDATE_GUID, action: A.UPDATE, layout: MetaLayout.Default } },
  },
  Sbr: {
    Icon: 'tabler-file-check',
    Submissions: {
      Title: 'Submissions',
      Name: 'apps-sbr-submissions',
      Icon: 'tabler-list-details',
      Meta: { subject: Subject.SBR_SUBMISSIONS, action: A.READ, layout: MetaLayout.Default },
    },
    Submission: {
      Title: 'Submission detail',
      Name: 'apps-sbr-submissions-guid',
      Meta: { subject: Subject.SBR_SUBMISSIONS_GUID, action: A.READ, layout: MetaLayout.Default },
    },
  },
  Auth: {
    login: {
      title: 'Login',
      name: 'login',
      meta: {
        action: 'read',
        subject: Subject.AUTH_LOGIN,
        layout: MetaLayout.Blank,
        redirectIfLoggedIn: true,
      },
    },
    forgotPassword: {
      title: 'Forgot Password',
      name: 'forgot-password',
      meta: {
        action: 'read',
        subject: Subject.AUTH_FORGOT_PASSWORD,
        layout: MetaLayout.Blank,
        redirectIfLoggedIn: true,
      },
    },
    setPassword: {
      title: 'Set Password',
      name: 'set-password',
      meta: {
        action: 'read',
        subject: Subject.AUTH_SET_PASSWORD,
        layout: MetaLayout.Blank,
      },
    },
    lobby: {
      title: 'Please Wait',
      name: 'auth-lobby',
      meta: { subject: Subject.AUTH_LOBBY, layout: MetaLayout.Blank },
    },
    waiting: {
      title: 'Please Wait',
      name: 'auth-waiting',
      meta: { subject: Subject.AUTH_WAITING, layout: MetaLayout.Blank },
    },
    notAuthorized: {
      title: 'Not Authorized',
      name: 'not-authorized',
      meta: { subject: Subject.AUTH_NOT_AUTHORIZED, layout: MetaLayout.Blank },
    },
  },

  Dashboard: {
    ecommerce: {
      icon: 'tabler-smart-home',
      title: 'Dashboard',
      name: 'dashboards-ecommerce',
      meta: { subject: Subject.DASHBOARDS_ECOMMERCE, layout: MetaLayout.Default },
    },
  },

  AiWorkspace: {
    Icon: 'tabler-sparkles',
    Chat: {
      Icon: 'tabler-sparkles',
      Title: 'AI Workspace',
      Name: 'ai-workspace',
      Meta: {
        subject: Subject.AI_THREADS,
        action: A.READ,
        layout: MetaLayout.AiWorkspace,
        shortcuts: Object.values(AiWorkspaceShortcut),
      },
    },
  },

  Banking: {
    Icon: 'tabler-building-bank',
    Transactions: {
      Icon: 'tabler-receipt-2',
      Title: 'Transactions',
      Name: 'apps-banking-transactions',
      Meta: {
        subject: Subject.BANKING_TRANSACTIONS,
        layout: MetaLayout.Default,
        action: A.READ,
      },
    },
    Accounts: {
      Icon: 'tabler-list-details',
      Title: 'Accounts',
      Name: 'apps-banking-accounts',
      Meta: {
        subject: Subject.BANKING_ACCOUNTS,
        layout: MetaLayout.Default,
        action: A.READ,
      },
    },
    Rules: {
      Icon: 'tabler-filter-check',
      Title: 'Rules',
      Name: 'apps-banking-rules',
      Meta: {
        subject: Subject.BANKING_RULES,
        layout: MetaLayout.Default,
        action: A.READ,
      },
    },
    TaxCodes: {
      Icon: 'tabler-percentage',
      Title: 'Tax codes',
      Name: 'apps-banking-tax-codes',
      Meta: {
        subject: Subject.BANKING_TAX_CODES,
        layout: MetaLayout.Default,
        action: A.READ,
      },
    },
    Journals: {
      Icon: 'tabler-notebook',
      Title: 'Journals',
      Name: 'apps-banking-journals',
      Meta: {
        subject: Subject.BANKING_JOURNALS,
        layout: MetaLayout.Default,
        action: A.READ,
      },
    },
    Imports: {
      Icon: 'tabler-file-import',
      Title: 'Imports',
      Name: 'apps-banking-imports',
      Meta: {
        subject: Subject.BANKING_TRANSACTIONS,
        layout: MetaLayout.Default,
        action: A.READ,
      },
    },
  },

  Orders: {
    Endpoint: Endpoint.Orders,
    Icon: 'tabler-shopping-bag',
    service: {
      component: OrderSP,
      title: 'Order ',
      path: '/service-o-:guid',
      name: 'service-o-guid',
      meta: { subject: Subject.ORDERS_PREVIEW_SERVICE, layout: MetaLayout.Margin },
    },
    Service: {
      Component: OrderSP,
      Icon: 'tabler-world-bolt',
      Title: 'Order ',
      Path: '/service-o-:guid',
      Name: 'service-o-guid',
      Meta: { subject: Subject.ORDERS_PREVIEW_SERVICE, layout: MetaLayout.Margin },
    },

    List: {
      Icon: 'tabler-shopping-bag',
      Title: 'All Orders',
      Name: 'apps-order-list',
      Meta: { subject: Subject.ORDERS_PREVIEW_ALL, layout: MetaLayout.Default },
    },

    Add: {
      Icon: 'tabler-plus',
      Title: 'Create Order',
      Name: 'apps-order-add',
      Meta: { subject: Subject.ORDERS_CREATE, layout: MetaLayout.Default },
    },

    Preview: {
      Icon: 'tabler-eye',
      Title: 'Preview Order',
      Tooltip: null,
      Name: 'apps-order-preview-guid',
      Meta: { subject: Subject.ORDERS_PREVIEW_GUID, layout: MetaLayout.Default },
    },

    Search: {
      Icon: themeConfig.icons.search.icon,
      Title: 'Search Orders',
      Name: null,
      Meta: { subject: Subject.ORDERS_SEARCH, layout: null },
    },

    Invoices: {
      Icon: 'tabler-file-dollar',
      Title: 'Order Invoices',
      Name: null,
      Meta: { subject: Subject.ORDERS_INVOICES, layout: null },
    },

    TableShortcuts: {
      Icon: 'tabler-keyboard',
      Title: 'Shortcuts',
      Name: null,
      Meta: { subject: Subject.ORDERS_TABLE_SHORTCUTS, layout: null },
    },
    SetInactive: {
      Icon: 'tabler-edit-off',
      Title: 'Mark Inactive',
      Tooltip: 'Mark Order as Inactive',
      Name: null,
      Meta: { subject: Subject.ORDERS_ACTIONS_SET_INACTIVE, layout: null },
    },
  },

  Invoices: {
    Endpoint: Endpoint.Invoices,
    Icon: 'tabler-file-dollar',
    Service: {
      Component: InvoiceSP,
      Icon: 'tabler-world-bolt',
      Title: 'Invoice',
      Tooltip: 'Navigate to Invoice Service Page',
      Path: '/service-i-:guid',
      Name: 'service-i-guid',
      Meta: { subject: Subject.INVOICES_PREVIEW_SERVICE, layout: MetaLayout.Margin },
    },
    service: {
      // old
      component: InvoiceSP,
      Icon: 'tabler-world-bolt',
      title: 'Invoice',
      path: '/service-i-:guid',
      name: 'service-i-guid',
      meta: { subject: Subject.INVOICES_PREVIEW_SERVICE, layout: MetaLayout.Margin },
    },
    List: {
      Icon: 'tabler-file-dollar',
      Title: 'All Invoices',
      Name: 'apps-invoice-list',
      Meta: { subject: Subject.INVOICES_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    Add: {
      Icon: 'tabler-plus',
      Title: 'Create Invoice',
      Name: 'apps-invoice-add',
      Meta: { subject: Subject.INVOICES_CREATE, layout: MetaLayout.Default },
    },
    Edit: {
      Icon: 'tabler-pencil',
      Title: 'Edit Invoice',
      Name: 'apps-invoice-edit-guid',
      Meta: { subject: Subject.INVOICES_UPDATE_GUID, layout: MetaLayout.Default },
    },
    Preview: {
      Icon: 'tabler-eye',
      Title: 'Preview Invoice',
      Tooltip: null,
      Name: 'apps-invoice-preview-guid',
      Meta: { subject: Subject.INVOICES_PREVIEW_GUID, layout: MetaLayout.Default },
    },
    Refund: {
      Icon: 'tabler-droplet-dollar',
      Title: 'Refund Invoice',
      Tooltip: null,
      Name: null,
      Meta: { subject: Subject.INVOICES_REFUND_GUID, layout: MetaLayout.Default },
    },
    Search: {
      Icon: themeConfig.icons.search.icon,
      Title: 'Search Invoices',
      Name: null,
      Meta: { subject: Subject.INVOICES_SEARCH, layout: null },
    },
    Reconsile: {
      Icon: 'tabler-transaction-dollar',
      Title: 'Reconsile Invoices',
      Name: null,
      Meta: { subject: Subject.INVOICES_RECONSILE, layout: null },
    },
    TableMessagesTool: {
      Icon: 'tabler-mail',
      Title: 'Message Invoices',
      Name: null,
      Meta: { subject: Subject.INVOICES_MESSAGE, layout: null },
    },
    TableShortcuts: {
      Icon: 'tabler-keyboard',
      Title: 'Shortcuts',
      Name: null,
      Meta: { subject: Subject.INVOICES_TABLE_SHORTCUTS, layout: null },
    },
    SetInactive: {
      Icon: 'tabler-edit-off',
      Title: 'Mark Inactive',
      Tooltip: 'Mark Invoice as Inactive',
      Name: null,
      Meta: { subject: Subject.INVOICES_ACTIONS_SET_INACTIVE, layout: null },
    },
  },

  Quotes: {
    Endpoint: Endpoint.Quotes,
    Icon: 'tabler-file-invoice',
    service: {
      component: QuoteSP,
      icon: 'tabler-file-invoice',
      title: 'Quote ',
      path: '/service-q-:guid',
      name: 'service-q-guid',
      meta: { subject: Subject.QUOTES_PREVIEW_SERVICE, layout: MetaLayout.Margin },
    },
    Service: {
      Component: QuoteSP,
      Icon: 'tabler-file-invoice',
      Title: 'Quote ',
      Tooltip: 'Navigate to Quote Service Page',
      Path: '/service-q-:guid',
      Name: 'service-q-guid',
      Meta: { subject: Subject.QUOTES_PREVIEW_SERVICE, layout: MetaLayout.Margin },
    },
    List: {
      Icon: 'tabler-file-invoice',
      Title: 'All Quotes',
      Name: 'apps-quote-list',
      Meta: { subject: Subject.QUOTES_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    Preview: {
      Icon: 'tabler-eye',
      Title: 'Preview Quote',
      Name: 'apps-quote-preview-guid',
      Meta: { subject: Subject.QUOTES_PREVIEW_GUID, layout: MetaLayout.Default },
    },
    Add: {
      Icon: 'tabler-plus',
      Title: 'New Quote',
      Name: 'apps-quote-add',
      Meta: { subject: Subject.QUOTES, layout: MetaLayout.Default },
    },
    Edit: {
      Icon: 'tabler-pencil',
      Title: 'Edit Quote',
      Name: 'apps-quote-edit-guid',
      Meta: { subject: Subject.QUOTES_UPDATE_GUID, layout: null },
    },
    QuotesCalendarSidebar: {
      Icon: 'tabler-calendar',
      Title: 'Calendar',
      Name: null,
      Meta: { subject: Subject.QUOTES_CALENDAR_SIDEBAR, layout: null },
    },
    Search: {
      Icon: 'tabler-search',
      Title: 'Search Quotes',
      Name: null,
      Meta: { subject: Subject.QUOTES_SEARCH, layout: null },
    },
    TableMessagesTool: {
      Icon: 'tabler-mail',
      Title: 'Message Quotes',
      Name: null,
      Meta: { subject: Subject.QUOTES_MESSAGE, layout: null },
    },
    TableShortcuts: {
      Icon: 'tabler-keyboard',
      Title: 'Shortcuts',
      Name: null,
      Meta: { subject: Subject.QUOTES_TABLE_SHORTCUTS, layout: null },
    },
    TableInvoicesTool: {
      Icon: 'tabler-file-dollar',
      Title: 'Invoice Tools',
      Name: null,
      Meta: { subject: Subject.QUOTES_INVOICES_TOOL, layout: null },
    },
    SetInactive: {
      Icon: 'tabler-edit-off',
      Title: 'Mark Inactive',
      Tooltip: 'Mark Quote as Inactive',
      Name: null,
      Meta: { subject: Subject.QUOTES_ACTIONS_SET_INACTIVE, layout: null },
    },
    SetPurchaseOrder: {
      Icon: 'tabler-clipboard-check',
      Title: 'Set Purchase Order',
      Tooltip: 'Set Purchase Order for Quote',
      Name: null,
      Meta: { subject: Subject.QUOTES_ACTIONS_SET_PO, layout: null },
    }
  },

  JobTypes: {
    list: {
      title: 'Job Types',
      icon: 'tabler-briefcase-2',
      name: 'setup-job-types-list',
      meta: { subject: Subject.JOBTYPES_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    add: {
      icon: 'tabler-plus',
      title: 'Create New Job Type',
      name: 'setup-job-types-add',
      meta: { subject: Subject.JOBTYPES_CREATE, layout: MetaLayout.Default },
    },
    preview: {
      icon: 'tabler-eye',
      title: 'Job Type {id}',
      name: 'setup-job-types-preview-guid',
      meta: { subject: Subject.JOBTYPES_PREVIEW_GUID, layout: MetaLayout.Default },
    },

    TableShortcuts: {
      Icon: 'tabler-keyboard',
      Title: 'Shortcuts',
      Name: null,
      Meta: { subject: Subject.JOBTYPES_TABLE_SHORTCUTS, layout: null },
    },
  },

  Projects: {
    icon: 'tabler-backhoe',
    list: {
      title: 'All Projects',
      name: 'apps-projects-list',
      meta: { subject: Subject.PROJECTS_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    preview: {
      title: 'Project {id}',
      name: 'apps-projects-preview-guid',
      meta: { subject: Subject.PROJECTS_PREVIEW_GUID, layout: MetaLayout.Default },
    },
    Add: {
      Icon: 'tabler-plus',
      Title: 'Create Project',
      Name: '',
      Meta: { subject: Subject.PROJECTS_CREATE, layout: MetaLayout.Default },
    },
  },

  Assets: {
    icon: 'tabler-assembly',
    list: {
      icon: 'tabler-assembly',
      title: 'All Assets',
      name: 'apps-assets-list',
      meta: { subject: Subject.ASSETS_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    preview: {
      title: 'Asset {id}',
      name: 'apps-assets-preview-guid',
      meta: { subject: Subject.ASSETS_PREVIEW_GUID, layout: MetaLayout.Default },
    },
    add: {
      icon: 'tabler-plus',
      title: 'New Asset',
      name: '',
      meta: { subject: Subject.ASSETS_CREATE, layout: MetaLayout.Default },
    },
    TableShortcuts: {
      Icon: 'tabler-keyboard',
      Title: 'Shortcuts',
      Name: null,
      Meta: { subject: Subject.ASSETS_TABLE_SHORTCUTS, layout: null },
    },
  },

  Services: {
    Icon: 'tabler-settings-spark',
    List: {
      Icon: 'tabler-clock-cog',
      Title: 'Service History',
      Name: null,
      Meta: { subject: Subject.ASSETS_SERVICES_PREVIEW_ALL, layout: null },
    },
    Preview: {
      Icon: 'tabler-eye',
      Title: 'Service',
      Name: null,
      Meta: { subject: Subject.ASSETS_SERVICES_PREVIEW_GUID, layout: null },
    },
    Edit: {
      Icon: 'tabler-pencil',
      Title: 'Service',
      Name: null,
      Meta: { subject: Subject.ASSETS_SERVICES_UPDATE_GUID, layout: null },
    },
    Add: {
      Icon: 'tabler-plus',
      Title: 'Add Service Entry',
      Name: null,
      Meta: { subject: Subject.ASSETS_SERVICES_CREATE, layout: null },
    },
  },

  Inspections: {
    Icon: 'tabler-eye-bolt',
    List: {
      Icon: 'tabler-eye-cog',
      Title: 'Inspection History',
      Name: null,
      Meta: { subject: Subject.ASSETS_INSPECTIONS_PREVIEW_ALL, layout: null },
    },
    Preview: {
      Icon: 'tabler-eye',
      Title: 'Inspection',
      Name: null,
      Meta: { subject: Subject.ASSETS_INSPECTIONS_PREVIEW_GUID, layout: null },
    },
    Edit: {
      Icon: 'tabler-pencil',
      Title: 'Inspection',
      Name: null,
      Meta: { subject: Subject.ASSETS_INSPECTIONS_UPDATE_GUID, layout: null },
    },
    Add: {
      Icon: 'tabler-plus',
      Title: 'Add Inspection Entry',
      Name: null,
      Meta: { subject: Subject.ASSETS_INSPECTIONS_CREATE, layout: null },
    },
  },

  // CRM
  Calendar: {
    Endpoint: Endpoint.Calendar,
    icon: 'tabler-calendar',
    Add: {
      Icon: 'tabler-plus',
      Title: 'Add Calendar Entry',
      Name: null,
      Meta: { subject: Subject.CALENDAR_CREATE, layout: null },
    },
    AddRule: {
      Icon: 'tabler-shield-plus',
      Title: 'Add Rule Entry',
      Name: null,
      Meta: { subject: Subject.CALENDAR_CREATE_RULE, layout: null },
    },
    DeleteRule: {
      Icon: null,
      Title: 'Delete Rule Entry',
      Name: null,
      Meta: { subject: Subject.CALENDAR_DELETE_RULE, layout: null },
    },
    AddQuote: {
      Icon: 'tabler-file-invoice',
      Title: 'Add Quote Entry',
      Name: null,
      Meta: { subject: Subject.CALENDAR_CREATE_QUOTE, layout: null },
    },

    Full: {
      Icon: 'tabler-calendar',
      Title: 'Calendar',
      Name: 'apps-calendar-full',
      Meta: {
        subject: Subject.CALENDAR_PREVIEW_ALL,
        layout: MetaLayout.Default,
      },
    },
  },
  Customers: {
    icon: 'tabler-man',
    List: {
      Title: 'All Customers',
      Icon: 'tabler-man',
      Name: 'apps-customers-list',
      Meta: { subject: Subject.CUSTOMERS_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    preview: {
      title: 'Customer {id}',
      name: 'apps-customers-preview-guid',
      meta: { subject: Subject.CUSTOMERS_PREVIEW_GUID, layout: MetaLayout.Default },
    },
    edit: {
      title: 'Customer {id}',
      name: 'apps-customers-edit-guid',
      meta: { subject: Subject.CUSTOMERS_UPDATE_GUID, layout: MetaLayout.Default },
    },
    add: {
      icon: 'tabler-plus',
      title: 'Create Customer {id}',
      name: 'apps-customers-add-guid',
      meta: { subject: Subject.CUSTOMERS_CREATE, layout: MetaLayout.Default },
    },
    TableShortcuts: {
      Icon: 'tabler-keyboard',
      Title: 'Shortcuts',
      Name: null,
      Meta: { subject: Subject.CUSTOMERS_TABLE_SHORTCUTS, layout: null },
    },
  },
  Messages: {
    icon: 'tabler-mail',
    Icon: 'tabler-mail',

    TableMessagesTool: {
      Icon: 'tabler-send',
      Title: 'Messages Tool',
      Name: null, //TableMessagesTool.vue
      Meta: { subject: Subject.MESSAGES_TABLE_TOOL, layout: null },
    },

    List: {
      Icon: 'tabler-mail',
      Title: 'All Messages',
      Name: 'apps-messages-list',
      Meta: { subject: Subject.MESSAGES_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    list: {
      icon: 'tabler-mail',
      title: 'All Messages',
      name: 'apps-messages-list',
      meta: { subject: Subject.MESSAGES_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    Add: {
      Icon: 'tabler-plus',
      Title: 'Create Message',
      Name: 'apps-messages-add',
      Meta: { subject: Subject.MESSAGES_CREATE, layout: MetaLayout.Default },
    },
    Preview: {
      Icon: 'tabler-eye',
      Title: 'Preview Message',
      Tooltip: null,
      Name: 'apps-messages-preview-guid',
      Meta: { subject: Subject.MESSAGES_PREVIEW_GUID, layout: MetaLayout.Default },
    },
    Search: {
      Icon: themeConfig.icons.search.icon,
      Title: 'Search Messages',
      Name: null,
      Meta: { subject: Subject.MESSAGES_SEARCH, layout: null },
    },
    preview: {
      title: 'Message {id}',
      name: 'apps-messages-preview-guid',
      meta: { subject: Subject.MESSAGES_PREVIEW_GUID, layout: MetaLayout.Default },
    },
    edit: {
      title: 'Message {id}',
      name: '', //dialog
      meta: { subject: Subject.MESSAGES_UPDATE_GUID, layout: MetaLayout.Default },
    },
    TableShortcuts: {
      Icon: 'tabler-keyboard',
      Title: 'Shortcuts',
      Name: null,
      Meta: { subject: Subject.MESSAGES_TABLE_SHORTCUTS, layout: null },
    },
  },

  // Booking Rules
  Rules: {
    Endpoint: Endpoint.Rules,
    Icon: 'tabler-shield-pin',

    List: {
      Icon: 'tabler-shield-pin',
      Title: 'Booking Rules',
      Name: null,
      Meta: { subject: Subject.BOOKINGRULES_PREVIEW_ALL, layout: null },
    },
    preview: {
      icon: 'tabler-pencil',
      title: 'Preview Rule {id}',
      name: '',
      meta: { subject: Subject.BOOKINGRULES_PREVIEW_GUID, layout: MetaLayout.Default },
    },
  },

  // Setup
  Businesses: {
    icon: 'tabler-cat',
    List: {
      Icon: 'tabler-briefcase',
      Title: 'Businesses',
      Name: 'setup-businesses-list',
      Meta: { subject: Subject.BUSINESSES_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    preview: {
      icon: 'tabler-eye',
      title: 'Business {id}',
      name: 'setup-businesses-view-guid',
      meta: { subject: Subject.BUSINESSES_PREVIEW_GUID, layout: MetaLayout.Default },
    },
    edit: {
      icon: 'tabler-pencil',
      title: 'Business {id}',
      name: 'setup-businesses-edit-guid',
      meta: { subject: Subject.BUSINESSES_UPDATE_GUID, layout: MetaLayout.Default },
    },
    add: {
      icon: 'tabler-plus',
      title: 'New Business',
      name: 'setup-businesses-add-guid',
      meta: { subject: Subject.BUSINESSES_CREATE, layout: MetaLayout.Default },
    },

    TableShortcuts: {
      Icon: 'tabler-keyboard',
      Title: 'Shortcuts',
      Name: null,
      Meta: { subject: Subject.BUSINESSES_TABLE_SHORTCUTS, layout: null },
    },
  },

  RevenueStreams: {
    list: {
      icon: 'tabler-affiliate',
      title: 'Revenue Streams',
      name: 'setup-revenue-streams-list',
      meta: { subject: Subject.REVENUESTREAMS_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    add: {
      icon: 'tabler-plus',
      title: 'New Revenue Stream',
      name: '',
      meta: { subject: Subject.REVENUESTREAMS_CREATE, layout: MetaLayout.Default },
    },

    TableShortcuts: {
      Icon: 'tabler-keyboard',
      Title: 'Shortcuts',
      Name: null,
      Meta: { subject: Subject.REVENUESTREAMS_TABLE_SHORTCUTS, layout: null },
    },
  },

  Tools: {
    Icon: 'tabler-hammer',
    HoardingPlanCreator: {
      Icon: 'tabler-checklist',
      Title: 'Hoarding Planner',
      Name: null,
      Meta: { subject: Subject.TOOLS_HOARDINGS_PLAN_CREATOR, layout: null },
      Tooltips: ['Click to create a hoarding plan'],
      ButtonColor: 'info',
    },
  },

  Products: {
    Endpoint: Endpoint.Products,
    Icon: 'tabler-list-check',
    List: {
      Icon: 'tabler-list-check',
      Title: 'Products',
      Name: 'apps-products-list',
      Meta: { subject: Subject.PRODUCTS_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    Preview: {
      Icon: 'tabler-eye',
      Title: 'Preview Product',
      Name: 'apps-products-preview-guid',
      Meta: { subject: Subject.PRODUCTS_PREVIEW_GUID, layout: MetaLayout.Default },
    },
    Search: {
      Icon: themeConfig.icons.search.icon,
      Title: 'Search Products',
      Name: null,
      Meta: { subject: Subject.PRODUCTS_PREVIEW_ALL, layout: null },
    },
    TableShortcuts: {
      Icon: 'tabler-keyboard',
      Title: 'Shortcuts',
      Name: null,
      Meta: { subject: Subject.PRODUCTS_PREVIEW_ALL, layout: null },
    },
  },

  Categories: {
    Endpoint: Endpoint.ProductCategories,
    Icon: 'tabler-category',
    List: {
      Icon: 'tabler-category',
      Title: 'Categories',
      Tooltip: 'View all product categories',
      Name: 'apps-product-categories-list',
      Meta: { subject: Subject.PRODUCTS_CATEGORIES_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    Preview: {
      Icon: 'tabler-eye',
      Title: 'Preview Category',
      Tooltip: 'View category details',
      Name: 'apps-product-categories-preview-guid',
      Meta: { subject: Subject.PRODUCTS_CATEGORIES_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    Add: {
      Icon: 'tabler-plus',
      Title: 'New Category',
      Name: null,
      Meta: { subject: Subject.PRODUCTS_CATEGORIES_PREVIEW_ALL, layout: null },
    },
    Search: {
      Icon: themeConfig.icons.search.icon,
      Title: 'Search Categories',
      Name: null,
      Meta: { subject: Subject.PRODUCTS_CATEGORIES_PREVIEW_ALL, layout: null },
    },
    TableShortcuts: {
      Icon: 'tabler-keyboard',
      Title: 'Shortcuts',
      Name: null,
      Meta: { subject: Subject.PRODUCTS_CATEGORIES_PREVIEW_ALL, layout: null },
    },
  },

  BookingOptions: {
    Icon: 'tabler-list-check',
    List: {
      Icon: 'tabler-list-check',
      Title: 'Booking Options',
      Name: 'apps-product-booking-options-list',
      Meta: { subject: Subject.PRODUCTS_BOOKINGOPTIONS_PREVIEW_ALL, layout: MetaLayout.Default },
    },
  },

  Selections: {
    Icon: 'tabler-list-check',
    List: {
      Icon: 'tabler-list-check',
      Title: 'Selections',
      Name: 'apps-booking-option-selections-list',
      Meta: {
        subject: Subject.PRODUCTS_BOOKINGOPTIONS_SELECTIONS_PREVIEW_ALL,
        layout: MetaLayout.Default,
      },
    },
  },
  EmailAccounts: {
    Icon: 'tabler-mail-spark',
    List: {
      Icon: 'tabler-mail-spark',
      Title: 'Email Accounts',
      Name: 'setup-emails-email-accounts-list',
      Meta: { subject: Subject.EMAILACCOUNTS_PREVIEW_ALL, layout: MetaLayout.Default },
    },
  },
  Templates: {
    Icon: 'tabler-cat',
    List: {
      Icon: 'tabler-cat',
      Title: 'Templates',
      Name: 'setup-emails-templates-list',
      Meta: { subject: Subject.MESSAGES_TEMPLATES_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    Preview: {
      Icon: 'tabler-eye',
      Title: 'Preview Template',
      Name: 'setup-emails-templates-preview-guid',
      Meta: { subject: Subject.MESSAGES_TEMPLATES_PREVIEW_GUID, layout: MetaLayout.Default },
    },
  },
  Themes: {
    Icon: 'tabler-cat',
    List: {
      Icon: 'tabler-cat',
      Title: 'Themes',
      Name: 'setup-emails-themes-list',
      Meta: { subject: Subject.MESSAGES_THEMES_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    Preview: {
      Icon: 'tabler-eye',
      Title: 'Preview Theme',
      Name: 'setup-emails-themes-preview-guid',
      Meta: { subject: Subject.MESSAGES_THEMES_PREVIEW_GUID, layout: MetaLayout.Default },
    },
    Edit: {
      Icon: 'tabler-pencil',
      Title: 'Edit Theme',
      Name: 'setup-emails-themes-edit-guid',
      Meta: { subject: Subject.MESSAGES_THEMES_UPDATE_GUID, layout: MetaLayout.Default },
    },
  },

  Events: {
    Icon: 'tabler-timeline-event',
    List: {
      Icon: 'tabler-timeline-event',
      Title: 'Events',
      Name: 'setup-events-list',
      Meta: { subject: Subject.EVENTS_PREVIEW_ALL, layout: MetaLayout.Default },
    },
    Preview: {
      Icon: 'tabler-eye',
      Title: 'Event',
      Name: 'setup-events-preview-guid',
      Meta: { subject: Subject.EVENTS_GUID, layout: MetaLayout.Default },
    },
  },





  MicrosoftOAuth: {
    Icon: 'tabler-brand-windows',
    Settings: {
      Icon: 'tabler-brand-windows',
      Title: 'Microsoft OAuth',
      Name: 'setup-microsoft-oauth',
      Meta: { subject: Subject.MICROSOFTOAUTH_SETTINGS, layout: MetaLayout.Default },
    },
  },

  Sites: {
    Endpoint: Endpoint.Sites,
    Icon: 'tabler-world-www',
    List: {
      Icon: 'tabler-world-www',
      Title: 'Sites',
      Tooltip: 'View and manage tenant sites',
      Name: 'apps-sites-list',
      Meta: { subject: Subject.SITES, action: A.READ, layout: MetaLayout.Default },
    },
    Add: {
      Icon: 'tabler-plus',
      Title: 'Add Site',
      Tooltip: 'Provision a new tenant site',
      Name: 'apps-sites-add',
      Meta: { subject: Subject.SITES, layout: MetaLayout.Default, action: A.CREATE },
    },
    Edit: {
      Icon: 'tabler-pencil',
      Title: 'Edit Site',
      Tooltip: 'Update site configuration',
      Name: 'apps-sites-edit-guid',
      Meta: { subject: Subject.SITES_UPDATE_GUID, layout: MetaLayout.Default, action: A.UPDATE },
    },
    Preview: {
      Icon: 'tabler-eye',
      Title: 'Preview Site',
      Tooltip: 'View site configuration',
      Name: 'apps-sites-preview-guid',
      Meta: { subject: Subject.SITES_GUID, layout: MetaLayout.Default },
    },
    Search: {
      Icon: themeConfig.icons.search.icon,
      Title: 'Search Sites',
      Tooltip: 'Search tenant sites',
      Name: null,
      Meta: { subject: Subject.SITES, layout: null },
    },
    TableShortcuts: {
      Icon: 'tabler-keyboard',
      Title: 'Site shortcuts',
      Tooltip: 'Site list shortcuts',
      Name: null,
      Meta: { subject: Subject.SITES, layout: null },
    },
  },

  /*
  Blanks: {
    Icon: 'tabler-cat',
    List: {
      Icon: 'tabler-cat',
      Title: 'Blanks',
      Name: 'apps-blanks-list',
      Meta: { subject: Subject.BLANKS_PREVIEW_ALL, layout: MetaLayout.Default }
    },
    Preview: {
      Icon: 'tabler-eye',
      Title: 'Blank {id}',
      Name: 'apps-blanks-preview-guid',
      Meta: { subject: Subject.BLANKS_PREVIEW_GUID, layout: MetaLayout.Default }
    },
    Edit: {
      Icon: 'tabler-pencil',
      Title: 'Blank {id}',
      Name: 'apps-blanks-edit-guid',
      Meta: { subject: Subject.BLANKS_UPDATE_GUID, layout: MetaLayout.Default }
    },
    Add: {
      Icon: 'tabler-plus',
      Title: 'Create New Blank',
      Name: 'apps-blanks-add-guid',
      Meta: { subject: Subject.BLANKS_CREATE, layout: MetaLayout.Default }
    },
  },
  */
} satisfies PageMetaSchema

export const PageMeta = pageMeta
